import _objectSpread from '@babel/runtime/helpers/esm/objectSpread2';
import _applyDecoratedDescriptor from '@babel/runtime/helpers/esm/applyDecoratedDescriptor';
import { ExtensionTag, chainableEditorState, findParentNode, getNodeType, isNodeSelection, PlainExtension, environment, extension, command, NodeExtension, ExtensionPriority, isBoolean, keyBinding, NamedShortcut, assertGet, isElementDomNode, findParentNodeOfType, getMatchString } from '@remirror/core';
import { ExtensionListMessages } from '@remirror/messages';
import { wrappingInputRule, InputRule } from '@remirror/pm/inputrules';
import { TextSelection, NodeSelection } from '@remirror/pm/state';
import { ExtensionListTheme } from '@remirror/theme';
import _createForOfIteratorHelper from '@babel/runtime/helpers/esm/createForOfIteratorHelper';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import { joinBackward } from '@remirror/pm/commands';
import { Fragment, Slice, NodeRange } from '@remirror/pm/model';
import { wrapInList, sinkListItem, liftListItem } from '@remirror/pm/schema-list';
import { ReplaceAroundStep, canJoin, canSplit, liftTarget } from '@remirror/pm/transform';

function isList(type) {
  var _type$spec$group;

  return !!((_type$spec$group = type.spec.group) !== null && _type$spec$group !== void 0 && _type$spec$group.includes(ExtensionTag.ListContainerNode));
}
function isListItem(type) {
  var _type$spec$group2;

  return !!((_type$spec$group2 = type.spec.group) !== null && _type$spec$group2 !== void 0 && _type$spec$group2.includes(ExtensionTag.ListItemNode));
}
function isListNode(node) {
  return isList(node.type);
}
function isListItemNode(node) {
  return isListItem(node.type);
}

/**
 * Toggles a list.
 *
 * @remarks
 *
 * When the provided list wrapper is inactive (e.g. ul) then wrap the list with
 * this type. When it is active then remove the selected line from the list.
 *
 * @param listType - the list node type
 * @param itemType - the list item node type
 */

function toggleList(listType, itemType) {
  return props => {
    var dispatch = props.dispatch,
        tr = props.tr;
    var state = chainableEditorState(tr, props.state);
    var _tr$selection = tr.selection,
        $from = _tr$selection.$from,
        $to = _tr$selection.$to;
    var range = $from.blockRange($to);

    if (!range) {
      return false;
    }

    var parentList = findParentNode({
      predicate: node => isList(node.type),
      selection: tr.selection
    });

    if ( // the selection range is right inside the list
    parentList && range.depth - parentList.depth <= 1 && // the selectron range is the first child of the list
    range.startIndex === 0) {
      if (parentList.node.type === listType) {
        return liftListItemOutOfList(itemType)(props);
      }

      if (isList(parentList.node.type)) {
        if (listType.validContent(parentList.node.content)) {
          dispatch === null || dispatch === void 0 ? void 0 : dispatch(tr.setNodeMarkup(parentList.pos, listType));
          return true;
        } // When you try to toggle a bullet list into a task list or vice versa, since these two lists
        // use different type of list items, you can't directly change the list type.


        if (deepChangeListType(tr, parentList, listType, itemType)) {
          dispatch === null || dispatch === void 0 ? void 0 : dispatch(tr.scrollIntoView());
          return true;
        }

        return false;
      }
    }

    return wrapInList(listType)(state, dispatch);
  };
}
/**
 * Build a command that splits a non-empty textblock at the top level
 * of a list item by also splitting that list item.
 */

function splitListItem(listItemTypeOrName) {
  var ignoreAttrs = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : ['checked'];
  return function (_ref) {
    var tr = _ref.tr,
        dispatch = _ref.dispatch,
        state = _ref.state;
    var listItemType = getNodeType(listItemTypeOrName, state.schema);
    var _tr$selection2 = tr.selection,
        $from = _tr$selection2.$from,
        $to = _tr$selection2.$to;

    if ( // Don't apply to node selection where the selected node is a block (inline nodes might be okay)
    // eslint-disable-next-line unicorn/consistent-destructuring
    isNodeSelection(tr.selection) && tr.selection.node.isBlock || // List items can only exists at a depth of 2 or greater
    $from.depth < 2 || // Don't apply to a selection which spans multiple nodes.
    !$from.sameParent($to)) {
      return false;
    } // Get the grandparent of the start to make sure that it has the same type
    // as the list item type.


    var grandParent = $from.node(-1);

    if (grandParent.type !== listItemType) {
      return false;
    }

    if ($from.parent.content.size === 0 && $from.node(-1).childCount === $from.indexAfter(-1)) {
      // In an empty block. If this is a nested list, the wrapping
      // list item should be split. Otherwise, bail out and let next
      // command handle lifting.
      if ($from.depth === 2 || $from.node(-3).type !== listItemType || $from.index(-2) !== $from.node(-2).childCount - 1) {
        return false;
      }

      if (dispatch) {
        var _listItemType$content;

        var keepItem = $from.index(-1) > 0;
        var wrap = Fragment.empty; // Build a fragment containing empty versions of the structure
        // from the outer list item to the parent node of the cursor

        for (var depth = $from.depth - (keepItem ? 1 : 2); depth >= $from.depth - 3; depth--) {
          wrap = Fragment.from($from.node(depth).copy(wrap));
        }

        var content = ((_listItemType$content = listItemType.contentMatch.defaultType) === null || _listItemType$content === void 0 ? void 0 : _listItemType$content.createAndFill()) || undefined;
        wrap = wrap.append(Fragment.from(listItemType.createAndFill(null, content) || undefined));
        tr.replace($from.before(keepItem ? undefined : -1), $from.after(-3), new Slice(wrap, keepItem ? 3 : 2, 2));
        tr.setSelection(tr.selection.constructor.near(tr.doc.resolve($from.pos + (keepItem ? 3 : 2))));
        dispatch(tr.scrollIntoView());
      }

      return true;
    } // If the current list item is closed, when we split it, we'll keep its
    // content stay at the origin list item instead of the new list item.
    //
    // Since it's complex to implement, we only handle the most common case for
    // now: when the selection is inside one list item and the selection is at
    // the end of the first child (paragraph if using the default schema) of
    // this list item.


    if (grandParent.attrs.closed && $from.sameParent($to) && $to.pos === $to.end()) {
      if (dispatch) {
        var _listItemType$content2;

        var newListItemStartPos = $from.after(-1);

        var _content = ((_listItemType$content2 = listItemType.contentMatch.defaultType) === null || _listItemType$content2 === void 0 ? void 0 : _listItemType$content2.createAndFill()) || undefined;

        var newListItem = listItemType.createAndFill(null, _content);

        if (newListItem) {
          tr.insert(newListItemStartPos, newListItem);
          tr.setSelection(TextSelection.create(tr.doc, newListItemStartPos + 1));
        }

        tr.delete($from.pos, $to.pos);
        dispatch(tr.scrollIntoView());
      }

      return true;
    }

    var listItemAttributes = Object.fromEntries(Object.entries(grandParent.attrs).filter(_ref2 => {
      var _ref3 = _slicedToArray(_ref2, 1),
          attr = _ref3[0];

      return !ignoreAttrs.includes(attr);
    })); // The content inside the list item (e.g. paragraph)

    var contentType = $to.pos === $from.end() ? grandParent.contentMatchAt(0).defaultType : null;

    var contentAttributes = _objectSpread({}, $from.node().attrs);

    tr.delete($from.pos, $to.pos);
    var types = contentType ? [{
      type: listItemType,
      attrs: listItemAttributes
    }, {
      type: contentType,
      attrs: contentAttributes
    }] : [{
      type: listItemType,
      attrs: listItemAttributes
    }];

    if (!canSplit(tr.doc, $from.pos, 2)) {
      // I can't use `canSplit(tr.doc, $from.pos, 2, types)` and I don't know why
      return false;
    }

    if (dispatch) {
      // @ts-expect-error TODO: types for `tr.split` need to be fixed in `@types/prosemirror-transform`
      dispatch(tr.split($from.pos, 2, types).scrollIntoView());
    }

    return true;
  };
}

/**
 * Get all list item node type names in currect schema
 */
function getAllListItemNames(allExtensions) {
  return allExtensions.filter(extension => extension.tags.includes(ExtensionTag.ListItemNode)).map(extension => extension.name);
}
/**
 * Get all list item node types from current selection. Sort from deepest to root.
 *
 * @deprecated
 */


function getOrderedListItemTypes(listItemNames, state) {
  var _state$selection = state.selection,
      $from = _state$selection.$from,
      $to = _state$selection.$to;
  var sharedDepth = $from.sharedDepth($to.pos);
  var listItemTypes = new Map();

  for (var depth = sharedDepth; depth >= 0; depth--) {
    var type = $from.node(depth).type;

    if (listItemNames.includes(type.name) && !listItemTypes.has(type.name)) {
      listItemTypes.set(type.name, type);
    }
  }

  return listItemTypes;
}
/**
 * Create a command to sink the list item around the selection down into an
 * inner list. Use this function if you get multiple list item nodes in your
 * schema.
 *
 * @deprecated use `indentList` instead.
 */


function sharedSinkListItem(allExtensions) {
  var listItemNames = getAllListItemNames(allExtensions);
  return _ref4 => {
    var dispatch = _ref4.dispatch,
        state = _ref4.state;
    var listItemTypes = getOrderedListItemTypes(listItemNames, state);

    var _iterator = _createForOfIteratorHelper(listItemTypes.values()),
        _step;

    try {
      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var type = _step.value;

        if (sinkListItem(type)(state, dispatch)) {
          return true;
        }
      } // if current selection is inside at lease one list item node, then we
      // always return true.

    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }

    return listItemTypes.size > 0;
  };
}
/**
 * Create a command to lift the list item around the selection up intoa wrapping
 * list. Use this function if you get multiple list item nodes in your schema.
 *
 * @deprecated use `dedentList` instead.
 */

function sharedLiftListItem(allExtensions) {
  var listItemNames = getAllListItemNames(allExtensions);
  return _ref5 => {
    var dispatch = _ref5.dispatch,
        state = _ref5.state;
    var listItemTypes = getOrderedListItemTypes(listItemNames, state);

    var _iterator2 = _createForOfIteratorHelper(listItemTypes.values()),
        _step2;

    try {
      for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
        var type = _step2.value;

        if (liftListItem(type)(state, dispatch)) {
          return true;
        }
      } // if current selection is inside at lease one list item node, then we
      // always return true.

    } catch (err) {
      _iterator2.e(err);
    } finally {
      _iterator2.f();
    }

    return listItemTypes.size > 0;
  };
}
/**
 * Change a bullet list into a task list or vice versa. These lists use different type of list items,
 * so you need to use this function to not only change the list type but also change the list item type.
 */

function deepChangeListType(tr, foundList, listType, itemType) {
  var oldList = foundList.node;
  var $start = tr.doc.resolve(foundList.start);
  var listParent = $start.node(-1);
  var indexBefore = $start.index(-1);

  if (!listParent) {
    return false;
  }

  if (!listParent.canReplace(indexBefore, indexBefore + 1, Fragment.from(listType.create()))) {
    return false;
  }

  var newItems = [];

  for (var index = 0; index < oldList.childCount; index++) {
    var oldItem = oldList.child(index);

    if (!itemType.validContent(oldItem.content)) {
      return false;
    }

    var newItem = itemType.createChecked(null, oldItem.content);
    newItems.push(newItem);
  }

  var newList = listType.createChecked(null, newItems);
  var start = foundList.pos;
  var end = start + oldList.nodeSize;
  var from = tr.selection.from;
  tr.replaceRangeWith(start, end, newList);
  tr.setSelection(tr.selection.constructor.near(tr.doc.resolve(from)));
  return true;
}
/**
 * Wraps list items in `range` to a list.
 */


function wrapItems(_ref6) {
  var _slice$content$firstC;

  var listType = _ref6.listType,
      itemType = _ref6.itemType,
      tr = _ref6.tr,
      range = _ref6.range;
  var oldList = range.parent; // A slice that contianes all selected list items

  var slice = tr.doc.slice(range.start, range.end);

  if (oldList.type === listType && ((_slice$content$firstC = slice.content.firstChild) === null || _slice$content$firstC === void 0 ? void 0 : _slice$content$firstC.type) === itemType) {
    return false;
  }

  var newItems = [];

  for (var i = 0; i < slice.content.childCount; i++) {
    var oldItem = slice.content.child(i);

    if (!itemType.validContent(oldItem.content)) {
      return false;
    }

    var newItem = itemType.createChecked(null, oldItem.content);
    newItems.push(newItem);
  }

  var newList = listType.createChecked(null, newItems);
  tr.replaceRange(range.start, range.end, new Slice(Fragment.from(newList), 0, 0));
  return true;
}
/**
 * Wraps existed list items to a new type of list, which only containes these list items.
 *
 * @remarks
 *
 * @example
 *
 * Here is some pseudo-code to show the purpose of this function:
 *
 * before:
 *
 * ```html
 *  <ul>
 *    <li>item A</li>
 *    <li>item B<!-- cursor_start --></li>
 *    <li>item C<!-- cursor_end --></li>
 *    <li>item D</li>
 *  </ul>
 * ```
 *
 * after:
 *
 * ```html
 *  <ul>
 *    <li>item A</li>
 *  </ul>
 *  <ol>
 *    <li>item B<!-- cursor_start --></li>
 *    <li>item C<!-- cursor_end --></li>
 *  </ol>
 *  <ul>
 *    <li>item D</li>
 *  </ul>
 * ```
 *
 * @alpha
 */


function wrapSelectedItems(_ref7) {
  var listType = _ref7.listType,
      itemType = _ref7.itemType,
      tr = _ref7.tr;
  var range = calculateItemRange(tr.selection);

  if (!range) {
    return false;
  }

  var atStart = range.startIndex === 0;
  var _tr$selection3 = tr.selection,
      from = _tr$selection3.from,
      to = _tr$selection3.to;

  if (!wrapItems({
    listType,
    itemType,
    tr,
    range
  })) {
    return false;
  }

  tr.setSelection(new TextSelection(tr.doc.resolve(atStart ? from : from + 2), tr.doc.resolve(atStart ? to : to + 2)));
  tr.scrollIntoView();
  return true;
} // Copied from `prosemirror-schema-list`

function liftOutOfList(state, dispatch, range) {
  var tr = state.tr,
      list = range.parent;
  var originMappingLength = tr.mapping.maps.length; // Merge the list items into a single big item

  for (var pos = range.end, i = range.endIndex - 1, e = range.startIndex; i > e; i--) {
    pos -= list.child(i).nodeSize;
    tr.delete(pos - 1, pos + 1);
  }

  var $start = tr.doc.resolve(range.start),
      item = $start.nodeAfter;

  if (!item) {
    return false;
  }

  if (tr.mapping.slice(originMappingLength).map(range.end) !== range.start + item.nodeSize) {
    return false;
  }

  var atStart = range.startIndex === 0,
      atEnd = range.endIndex === list.childCount;
  var parent = $start.node(-1),
      indexBefore = $start.index(-1);

  if (!parent.canReplace(indexBefore + (atStart ? 0 : 1), indexBefore + 1, item.content.append(atEnd ? Fragment.empty : Fragment.from(list)))) {
    return false;
  }

  var start = $start.pos,
      end = start + item.nodeSize; // Strip off the surrounding list. At the sides where we're not at
  // the end of the list, the existing list is closed. At sides where
  // this is the end, it is overwritten to its end.

  tr.step(new ReplaceAroundStep(start - (atStart ? 1 : 0), end + (atEnd ? 1 : 0), start + 1, end - 1, new Slice((atStart ? Fragment.empty : Fragment.from(list.copy(Fragment.empty))).append(atEnd ? Fragment.empty : Fragment.from(list.copy(Fragment.empty))), atStart ? 0 : 1, atEnd ? 0 : 1), atStart ? 0 : 1));
  dispatch(tr.scrollIntoView());
  return true;
}

function maybeJoinList(tr, $pos) {
  var $from = $pos || tr.selection.$from;
  var joinable = [];
  var index;
  var parent;
  var before;
  var after;

  for (var depth = $from.depth; depth >= 0; depth--) {
    parent = $from.node(depth); // join backward

    index = $from.index(depth);
    before = parent.maybeChild(index - 1);
    after = parent.maybeChild(index);

    if (before && after && before.type.name === after.type.name && isListNode(before)) {
      var pos = $from.before(depth + 1);
      joinable.push(pos);
    } // join forward


    index = $from.indexAfter(depth);
    before = parent.maybeChild(index - 1);
    after = parent.maybeChild(index);

    if (before && after && before.type.name === after.type.name && isListNode(before)) {
      var _pos = $from.after(depth + 1);

      joinable.push(_pos);
    }
  } // sort `joinable` reversely


  joinable = [...new Set(joinable)].sort((a, b) => b - a);
  var updated = false;

  var _iterator3 = _createForOfIteratorHelper(joinable),
      _step3;

  try {
    for (_iterator3.s(); !(_step3 = _iterator3.n()).done;) {
      var _pos2 = _step3.value;

      if (canJoin(tr.doc, _pos2)) {
        tr.join(_pos2);
        updated = true;
      }
    }
  } catch (err) {
    _iterator3.e(err);
  } finally {
    _iterator3.f();
  }

  return updated;
}
/**
 * Build a command to lift the content inside a list item around the selection
 * out of list
 */

function liftListItemOutOfList(itemType) {
  return props => {
    var dispatch = props.dispatch,
        tr = props.tr;
    var state = chainableEditorState(tr, props.state);
    var range = getItemRange(itemType, tr.selection);

    if (!range) {
      return false;
    }

    if (!dispatch) {
      return true;
    }

    liftOutOfList(state, dispatch, range);
    return true;
  };
}
/**
 * @deprecated
 */

function getItemRange(itemType, selection) {
  var $from = selection.$from,
      $to = selection.$to;
  var range = $from.blockRange($to, node => {
    var _node$firstChild;

    return ((_node$firstChild = node.firstChild) === null || _node$firstChild === void 0 ? void 0 : _node$firstChild.type) === itemType;
  });
  return range;
}
/**
 * Returns a range that include all selected list items.
 */


function calculateItemRange(selection) {
  var $from = selection.$from,
      $to = selection.$to;
  return $from.blockRange($to, isListNode);
}
/**
 * Wraps selected list items to fit the list type and list item type in the
 * previous list.
 */

function wrapListBackward(tr) {
  var $cursor = tr.selection.$from;
  var range = $cursor.blockRange();

  if (!range || !isListItemNode(range.parent) || range.startIndex !== 0) {
    return false;
  }

  var root = $cursor.node(range.depth - 2); // the node that contains the list

  var itemIndex = $cursor.index(range.depth); // current node is the n-th node in item

  var listIndex = $cursor.index(range.depth - 1); // current item is the n-th item in list

  var rootIndex = $cursor.index(range.depth - 2); // current list is the n-th node in root

  var previousList = root.maybeChild(rootIndex - 1);
  var previousListItem = previousList === null || previousList === void 0 ? void 0 : previousList.lastChild;

  if ( // current node must be the first node in its parent list item;
  itemIndex !== 0 || // current list item must be the first list item in its parent list;
  listIndex !== 0) {
    return false;
  }

  if ( // there is a list before current list;
  previousList && isListNode(previousList) && // we can find the list item type for previousList;
  previousListItem && isListItemNode(previousListItem)) {
    return wrapSelectedItems({
      listType: previousList.type,
      itemType: previousListItem.type,
      tr: tr
    });
  }

  if (isListItemNode(root)) {
    var parentListItem = root;
    var parentList = $cursor.node(range.depth - 3);

    if (isListNode(parentList)) {
      return wrapSelectedItems({
        listType: parentList.type,
        itemType: parentListItem.type,
        tr: tr
      });
    }
  }

  return false;
}

function listBackspace(_ref8) {
  var view = _ref8.view;

  if (!view) {
    return false;
  }

  {
    var $cursor = view.state.selection.$cursor;

    if (!$cursor || $cursor.parentOffset > 0) {
      return false;
    }

    var range = $cursor.blockRange();

    if (!range || !isListItemNode(range.parent) || range.startIndex !== 0) {
      return false;
    }
  }
  {
    var tr = view.state.tr;

    if (wrapListBackward(tr)) {
      view.dispatch(tr);
    }
  }
  {
    var _$cursor = view.state.selection.$cursor;

    if (!_$cursor || _$cursor.parentOffset > 0) {
      return false;
    }

    var _range = _$cursor.blockRange();

    if (!_range || !isListItemNode(_range.parent) || _range.startIndex !== 0) {
      return false;
    }

    var itemIndex = _$cursor.index(_range.depth); // current node is the n-th node in item


    var listIndex = _$cursor.index(_range.depth - 1); // current item is the n-th item in list


    var rootIndex = _$cursor.index(_range.depth - 2); // current list is the n-th list in root


    if (itemIndex === 0 && listIndex === 0 && rootIndex <= 1) {
      liftListItem(_range.parent.type)(view.state, view.dispatch);
    }
  }
  joinBackward(view.state, view.dispatch, view);
  return true;
}

function createCustomMarkListItemNodeView(_ref) {
  var node = _ref.node,
      mark = _ref.mark,
      updateDOM = _ref.updateDOM,
      updateMark = _ref.updateMark;
  var markContainer = document.createElement('span');
  markContainer.contentEditable = 'false';
  markContainer.classList.add(ExtensionListTheme.LIST_ITEM_MARKER_CONTAINER);
  markContainer.append(mark);
  var contentDOM = document.createElement('div');
  var dom = document.createElement('li');
  dom.classList.add(ExtensionListTheme.LIST_ITEM_WITH_CUSTOM_MARKER);
  dom.append(markContainer);
  dom.append(contentDOM);

  var update = newNode => {
    if (newNode.type !== node.type) {
      return false;
    }

    node = newNode;
    updateDOM(node, dom);
    updateMark(node, mark);
    return true;
  };

  update(node);
  return {
    dom,
    contentDOM,
    update
  };
}

function findParentItem($from, range) {
  var parentItem = $from.node(range.depth - 1);
  var parentList = $from.node(range.depth - 2);

  if (!isListItemNode(parentItem) || !isListNode(parentList)) {
    return false;
  }

  return {
    parentItem,
    parentList
  };
}

function indentSiblingsOfItems(tr, range) {
  var selectedList = range.parent;
  var lastSelectedItem = range.parent.child(range.endIndex - 1);
  var endOfRange = range.end;
  var endOfSelectedList = range.$to.end(range.depth);

  if (endOfRange < endOfSelectedList) {
    // There are sibling items after the selected items, which must become
    // children of the last item
    tr.step(new ReplaceAroundStep(endOfRange - 1, endOfSelectedList, endOfRange, endOfSelectedList, new Slice(Fragment.from(lastSelectedItem.type.create(null, selectedList.copy())), 1, 0), 1, true));
    return new NodeRange(tr.doc.resolve(range.$from.pos), tr.doc.resolve(endOfSelectedList), range.depth);
  }

  return range;
}

function indentSiblingsOfList(tr, range) {
  var selectedList = range.parent;
  var lastSelectedItem = range.parent.child(range.endIndex - 1);
  var endOfSelectedList = range.end;
  var endOfParentListItem = range.$to.end(range.depth - 1);

  if (endOfSelectedList + 1 < endOfParentListItem) {
    // There are sibling nodes after the selected list, which must become
    // children of the last item
    tr.step(new ReplaceAroundStep(endOfSelectedList - 1, endOfParentListItem, endOfSelectedList + 1, endOfParentListItem, new Slice(Fragment.from(selectedList.type.create(null, lastSelectedItem.type.create(null))), 2, 0), 0, true));
    return new NodeRange(tr.selection.$from, tr.selection.$to, range.depth);
  }

  return range;
}

function changeItemsType(tr, range, parentList, parentItem) {
  var wrapped = wrapSelectedItems({
    listType: parentList.type,
    itemType: parentItem.type,
    tr
  });

  if (wrapped) {
    return new NodeRange(tr.selection.$from, tr.selection.$to, range.depth);
  }

  return range;
}
/**
 * A helper function to dedent selected list items.
 *
 * @beta
 */


function dedentList(tr) {
  var range = calculateItemRange(tr.selection); // debugger;

  if (!range) {
    return false;
  }

  var findParentItemResult = findParentItem(tr.selection.$from, range);

  if (!findParentItemResult) {
    return false;
  }

  var parentItem = findParentItemResult.parentItem,
      parentList = findParentItemResult.parentList;
  range = indentSiblingsOfItems(tr, range);
  range = indentSiblingsOfList(tr, range);
  range = changeItemsType(tr, range, parentList, parentItem);
  var target = liftTarget(range);

  if (typeof target !== 'number') {
    return true;
  }

  tr.lift(range, target);
  range = calculateItemRange(tr.selection);

  if (range) {
    maybeJoinList(tr, tr.doc.resolve(range.end - 2));
  }

  return true;
}
/**
 * @internal
 */

var dedentListCommand = _ref => {
  var tr = _ref.tr,
      dispatch = _ref.dispatch;

  if (dedentList(tr)) {
    dispatch === null || dispatch === void 0 ? void 0 : dispatch(tr.scrollIntoView());
  }

  return true;
};

/**
 * Try to find the previous item. Indent can only works if we can find this item.
 *
 * It may be the previous list item in the same list (in this case, `previousItem`
 * will be equal to `selectedList`), or it may be the last list item in the
 * previous list.
 */

function findPreviousItem(selectedList, $from, range) {
  var previousItem;
  var previousList;
  var previousItemStart;
  var previousListStart;
  var doc = $from.doc;

  if (range.startIndex >= 1) {
    previousItem = selectedList.child(range.startIndex - 1);
    previousList = selectedList;
    previousListStart = doc.resolve(range.start).start(range.depth);
    previousItemStart = previousListStart + 1;

    for (var i = 0; i < range.startIndex - 1; i++) {
      previousItemStart += previousList.child(i).nodeSize;
    }
  } else {
    var listIndex = $from.index(range.depth - 1);

    if (listIndex >= 1) {
      var listParent = $from.node(range.depth - 1);
      var listParentStart = $from.start(range.depth - 1);
      previousList = listParent.child(listIndex - 1);

      if (!isListNode(previousList)) {
        return false;
      }

      previousListStart = listParentStart + 1;

      for (var _i = 0; _i < listIndex - 1; _i++) {
        previousListStart += listParent.child(_i).nodeSize;
      }

      previousItem = previousList.child(previousList.childCount - 1);
      previousItemStart = previousListStart + previousList.nodeSize - previousItem.nodeSize - 1;

      if (!isListItemNode(previousItem)) {
        return false;
      }
    } else {
      return false;
    }
  }

  return {
    previousItem,
    previousList,
    previousItemStart,
    previousListStart
  };
}
/**
 * Separate selected list item into two slices: `selectedSlice` and `unselectedSlice`.
 *
 * If `unselectedSlice` exists, we don't want to change its indentation.
 */


function sliceSelectedItems(doc, $to, range) {
  var selectedSlice;
  var unselectedSlice;
  var start = range.start;
  var mid = $to.end(range.depth + 2);
  var end = range.end;

  if (mid + 1 >= end) {
    selectedSlice = doc.slice(start, end);
    unselectedSlice = null;
  } else {
    selectedSlice = doc.slice(start, mid);
    unselectedSlice = doc.slice(mid + 1, end - 1);
  }

  return {
    selectedSlice,
    unselectedSlice
  };
}
/**
 * A helper function to indent selected list items.
 *
 * @beta
 */


function indentList(tr) {
  var _tr$selection = tr.selection,
      $from = _tr$selection.$from,
      $to = _tr$selection.$to;
  var range = calculateItemRange(tr.selection);

  if (!range) {
    return false;
  }

  var selectedList = tr.doc.resolve(range.start).node();

  if (!isListNode(selectedList)) {
    return false;
  }

  var findPreviousItemResult = findPreviousItem(selectedList, $from, range);

  if (!findPreviousItemResult) {
    return false;
  }

  var previousItem = findPreviousItemResult.previousItem,
      previousList = findPreviousItemResult.previousList,
      previousItemStart = findPreviousItemResult.previousItemStart;

  var _sliceSelectedItems = sliceSelectedItems(tr.doc, $to, range),
      selectedSlice = _sliceSelectedItems.selectedSlice,
      unselectedSlice = _sliceSelectedItems.unselectedSlice;

  var newPreviousItemContent = previousItem.content.append(Fragment.fromArray([selectedList.copy(selectedSlice.content)])).append(unselectedSlice ? unselectedSlice.content : Fragment.empty);
  tr.deleteRange(range.start, range.end);
  var previousItemEnd = previousItemStart + previousItem.nodeSize - 2; // Note: nodeSize = end - start + 2

  var newPreviousItem = previousItem.copy(newPreviousItemContent);
  newPreviousItem.check();
  tr.replaceRangeWith(previousItemStart - 1, previousItemEnd + 1, newPreviousItem);
  tr.setSelection(previousList === selectedList ? TextSelection.create(tr.doc, $from.pos, $to.pos) : TextSelection.create(tr.doc, $from.pos - 2, $to.pos - 2));
  return true;
}
/**
 * @internal
 */

var indentListCommand = _ref => {
  var tr = _ref.tr,
      dispatch = _ref.dispatch;

  if (indentList(tr)) {
    dispatch === null || dispatch === void 0 ? void 0 : dispatch(tr.scrollIntoView());
  } // always return `true` since we don't want the browser to handle the keyboard event.


  return true;
};

/**
 * Provides some shared thing used by both `listItem` and `taskListItem`
 */

class ListItemSharedExtension extends PlainExtension {
  get name() {
    return 'listItemShared';
  }

  createKeymap() {
    var pcKeymap = {
      Tab: indentListCommand,
      'Shift-Tab': dedentListCommand,
      Backspace: listBackspace,
      'Mod-Backspace': listBackspace
    };

    if (environment.isMac) {
      var macKeymap = {
        'Ctrl-h': pcKeymap['Backspace'],
        'Alt-Backspace': pcKeymap['Mod-Backspace']
      };
      return _objectSpread(_objectSpread({}, pcKeymap), macKeymap);
    }

    return pcKeymap;
  }

  createPlugin() {
    return {
      appendTransaction: (_transactions, _oldState, newState) => {
        var tr = newState.tr;
        var updated = maybeJoinList(tr);
        return updated ? tr : null;
      }
    };
  }

}

var _dec$4, _dec2$3, _dec3$2, _class$4, _class2$2;
/**
 * Creates the node for a list item.
 */

var ListItemExtension = (_dec$4 = extension({
  defaultOptions: {
    enableCollapsible: false
  },
  staticKeys: ['enableCollapsible']
}), _dec2$3 = command(), _dec3$2 = command(), _dec$4(_class$4 = (_class2$2 = class ListItemExtension extends NodeExtension {
  get name() {
    return 'listItem';
  }

  createTags() {
    return [ExtensionTag.ListItemNode];
  }

  createNodeSpec(extra, override) {
    var _override$parseDOM;

    return _objectSpread(_objectSpread({
      content: 'paragraph block*',
      defining: true,
      draggable: false
    }, override), {}, {
      attrs: _objectSpread(_objectSpread({}, extra.defaults()), {}, {
        closed: {
          default: false
        },
        nested: {
          default: false
        }
      }),
      parseDOM: [{
        tag: 'li',
        getAttrs: extra.parse,
        priority: ExtensionPriority.Lowest // Make sure this rule has lower priority then `TaskListItemExtension`'s

      }, ...((_override$parseDOM = override.parseDOM) !== null && _override$parseDOM !== void 0 ? _override$parseDOM : [])],
      toDOM: node => {
        var attrs = extra.dom(node);
        return ['li', attrs, 0];
      }
    });
  }

  createNodeViews() {
    if (!this.options.enableCollapsible) {
      return {};
    }

    return (node, view, getPos) => {
      var mark = document.createElement('div');
      mark.classList.add(ExtensionListTheme.COLLAPSIBLE_LIST_ITEM_BUTTON);
      mark.contentEditable = 'false';
      mark.addEventListener('click', () => {
        if (mark.classList.contains('disabled')) {
          return;
        }

        var pos = getPos();
        var selection = NodeSelection.create(view.state.doc, pos);
        view.dispatch(view.state.tr.setSelection(selection));
        this.store.commands.toggleListItemClosed();
        return true;
      });
      return createCustomMarkListItemNodeView({
        mark,
        node,
        updateDOM: updateNodeViewDOM$1,
        updateMark: updateNodeViewMark$1
      });
    };
  }

  createKeymap() {
    return {
      Enter: splitListItem(this.type)
    };
  }

  createExtensions() {
    return [new ListItemSharedExtension()];
  }
  /**
   * Toggles the current list item.
   *
   * @param closed - the `closed` attribute. If it's a boolean value, then it
   * will be set as an attribute. If it's undefined, then the `closed` attribuate
   * will be toggled.
   */


  toggleListItemClosed(closed) {
    return _ref => {
      var _ref$state = _ref.state,
          tr = _ref$state.tr,
          selection = _ref$state.selection,
          dispatch = _ref.dispatch;

      // Make sure the list item is selected. Otherwise do nothing.
      if (!isNodeSelection(selection) || selection.node.type.name !== this.name) {
        return false;
      }

      var node = selection.node,
          from = selection.from;
      closed = isBoolean(closed) ? closed : !node.attrs.closed;
      dispatch === null || dispatch === void 0 ? void 0 : dispatch(tr.setNodeMarkup(from, undefined, _objectSpread(_objectSpread({}, node.attrs), {}, {
        closed
      })));
      return true;
    };
  }
  /**
   * Lift the content inside a list item around the selection out of list
   */


  liftListItemOutOfList(listItemType) {
    return liftListItemOutOfList(listItemType !== null && listItemType !== void 0 ? listItemType : this.type);
  }

}, (_applyDecoratedDescriptor(_class2$2.prototype, "toggleListItemClosed", [_dec2$3], Object.getOwnPropertyDescriptor(_class2$2.prototype, "toggleListItemClosed"), _class2$2.prototype), _applyDecoratedDescriptor(_class2$2.prototype, "liftListItemOutOfList", [_dec3$2], Object.getOwnPropertyDescriptor(_class2$2.prototype, "liftListItemOutOfList"), _class2$2.prototype)), _class2$2)) || _class$4);

function updateNodeViewDOM$1(node, dom) {
  node.attrs.closed ? dom.classList.add(ExtensionListTheme.COLLAPSIBLE_LIST_ITEM_CLOSED) : dom.classList.remove(ExtensionListTheme.COLLAPSIBLE_LIST_ITEM_CLOSED);
}

function updateNodeViewMark$1(node, mark) {
  node.childCount <= 1 ? mark.classList.add('disabled') : mark.classList.remove('disabled');
}

var _dec$3, _dec2$2, _dec3$1, _class$3, _class2$1;
/**
 * Create the node for a bullet list.
 */

var BulletListExtension = (_dec$3 = extension({
  defaultOptions: {
    enableSpine: false
  },
  staticKeys: ['enableSpine']
}), _dec2$2 = command({
  icon: 'listUnordered',
  label: _ref => {
    var t = _ref.t;
    return t(ExtensionListMessages.BULLET_LIST_LABEL);
  }
}), _dec3$1 = keyBinding({
  shortcut: NamedShortcut.BulletList,
  command: 'toggleBulletList'
}), _dec$3(_class$3 = (_class2$1 = class BulletListExtension extends NodeExtension {
  get name() {
    return 'bulletList';
  }

  createTags() {
    return [ExtensionTag.Block, ExtensionTag.ListContainerNode];
  }

  createNodeSpec(extra, override) {
    var _override$parseDOM;

    return _objectSpread(_objectSpread({
      content: 'listItem+'
    }, override), {}, {
      attrs: extra.defaults(),
      parseDOM: [{
        tag: 'ul',
        getAttrs: extra.parse
      }, ...((_override$parseDOM = override.parseDOM) !== null && _override$parseDOM !== void 0 ? _override$parseDOM : [])],
      toDOM: node => ['ul', extra.dom(node), 0]
    });
  }

  createNodeViews() {
    if (!this.options.enableSpine) {
      return {};
    }

    return (_, view, getPos) => {
      var _parentListItemNode$t;

      var dom = document.createElement('ul');
      dom.style.position = 'relative';
      var pos = getPos();
      var $pos = view.state.doc.resolve(pos + 1);
      var parentListItemNode = $pos.node($pos.depth - 1);
      var isFirstLevel = (parentListItemNode === null || parentListItemNode === void 0 ? void 0 : (_parentListItemNode$t = parentListItemNode.type) === null || _parentListItemNode$t === void 0 ? void 0 : _parentListItemNode$t.name) !== 'listItem';

      if (!isFirstLevel) {
        var parentListItemPos = $pos.start($pos.depth - 1);
        var spine = document.createElement('div');
        spine.contentEditable = 'false';
        spine.classList.add(ExtensionListTheme.LIST_SPINE);
        spine.addEventListener('click', event => {
          var selection = NodeSelection.create(view.state.doc, parentListItemPos - 1);
          view.dispatch(view.state.tr.setSelection(selection));
          this.store.commands.toggleListItemClosed();
          event.preventDefault();
          event.stopPropagation();
        });
        dom.append(spine);
      }

      var contentDOM = document.createElement('div');
      contentDOM.classList.add(ExtensionListTheme.UL_LIST_CONTENT);
      dom.append(contentDOM);
      return {
        dom,
        contentDOM
      };
    };
  }

  createExtensions() {
    return [new ListItemExtension({
      priority: ExtensionPriority.Low,
      enableCollapsible: this.options.enableSpine
    })];
  }
  /**
   * Toggle the bullet list for the current selection.
   */


  toggleBulletList() {
    return toggleList(this.type, assertGet(this.store.schema.nodes, 'listItem'));
  }

  listShortcut(props) {
    return this.toggleBulletList()(props);
  }

  createInputRules() {
    var regexp = /^\s*([*+-])\s$/;
    return [wrappingInputRule(regexp, this.type), new InputRule(regexp, (state, _match, start, end) => {
      var tr = state.tr;
      tr.deleteRange(start, end);
      var canUpdate = wrapSelectedItems({
        listType: this.type,
        itemType: assertGet(this.store.schema.nodes, 'listItem'),
        tr
      });

      if (!canUpdate) {
        return null;
      }

      return tr;
    })];
  }

}, (_applyDecoratedDescriptor(_class2$1.prototype, "toggleBulletList", [_dec2$2], Object.getOwnPropertyDescriptor(_class2$1.prototype, "toggleBulletList"), _class2$1.prototype), _applyDecoratedDescriptor(_class2$1.prototype, "listShortcut", [_dec3$1], Object.getOwnPropertyDescriptor(_class2$1.prototype, "listShortcut"), _class2$1.prototype)), _class2$1)) || _class$3);

var _dec$2, _dec2$1, _dec3, _class$2, _class2;
/**
 * Creates the list for the ordered list.
 */

var OrderedListExtension = (_dec$2 = extension({}), _dec2$1 = command({
  icon: 'listOrdered',
  label: _ref => {
    var t = _ref.t;
    return t(ExtensionListMessages.ORDERED_LIST_LABEL);
  }
}), _dec3 = keyBinding({
  shortcut: NamedShortcut.OrderedList,
  command: 'toggleOrderedList'
}), _dec$2(_class$2 = (_class2 = class OrderedListExtension extends NodeExtension {
  get name() {
    return 'orderedList';
  }

  createTags() {
    return [ExtensionTag.Block, ExtensionTag.ListContainerNode];
  }

  createNodeSpec(extra, override) {
    var _override$parseDOM;

    return _objectSpread(_objectSpread({
      content: 'listItem+'
    }, override), {}, {
      attrs: _objectSpread(_objectSpread({}, extra.defaults()), {}, {
        order: {
          default: 1
        }
      }),
      parseDOM: [{
        tag: 'ol',
        getAttrs: node => {
          var _node$getAttribute;

          if (!isElementDomNode(node)) {
            return {};
          }

          return _objectSpread(_objectSpread({}, extra.parse(node)), {}, {
            order: +((_node$getAttribute = node.getAttribute('start')) !== null && _node$getAttribute !== void 0 ? _node$getAttribute : 1)
          });
        }
      }, ...((_override$parseDOM = override.parseDOM) !== null && _override$parseDOM !== void 0 ? _override$parseDOM : [])],
      toDOM: node => {
        var extraAttributes = extra.dom(node);
        return node.attrs.order === 1 ? ['ol', extraAttributes, 0] : ['ol', _objectSpread(_objectSpread({}, extraAttributes), {}, {
          start: node.attrs.order
        }), 0];
      }
    });
  }
  /**
   * Automatically add the `ListItemExtension` which is required here.
   */


  createExtensions() {
    return [new ListItemExtension({
      priority: ExtensionPriority.Low
    })];
  }
  /**
   * Toggle the ordered list for the current selection.
   */


  toggleOrderedList() {
    return toggleList(this.type, assertGet(this.store.schema.nodes, 'listItem'));
  }

  listShortcut(props) {
    return this.toggleOrderedList()(props);
  }

  createInputRules() {
    var regexp = /^(\d+)\.\s$/;
    return [wrappingInputRule(regexp, this.type, match => ({
      order: +assertGet(match, 1)
    }), (match, node) => node.childCount + node.attrs.order === +assertGet(match, 1)), new InputRule(regexp, (state, match, start, end) => {
      var tr = state.tr;
      tr.deleteRange(start, end);
      var canUpdate = wrapSelectedItems({
        listType: this.type,
        itemType: assertGet(this.store.schema.nodes, 'listItem'),
        tr
      });

      if (!canUpdate) {
        return null;
      }

      var order = +assertGet(match, 1);

      if (order !== 1) {
        var found = findParentNodeOfType({
          selection: tr.selection,
          types: this.type
        });

        if (found) {
          tr.setNodeMarkup(found.pos, undefined, {
            order
          });
        }
      }

      return tr;
    })];
  }

}, (_applyDecoratedDescriptor(_class2.prototype, "toggleOrderedList", [_dec2$1], Object.getOwnPropertyDescriptor(_class2.prototype, "toggleOrderedList"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "listShortcut", [_dec3], Object.getOwnPropertyDescriptor(_class2.prototype, "listShortcut"), _class2.prototype)), _class2)) || _class$2);

var _dec$1, _class$1;
/**
 * Creates the node for a task list item.
 */

var TaskListItemExtension = (_dec$1 = command(), (_class$1 = class TaskListItemExtension extends NodeExtension {
  get name() {
    return 'taskListItem';
  }

  createTags() {
    return [ExtensionTag.ListItemNode];
  }

  createNodeSpec(extra, override) {
    var _override$parseDOM;

    return _objectSpread(_objectSpread({
      content: 'paragraph block*',
      defining: true,
      draggable: false
    }, override), {}, {
      attrs: _objectSpread(_objectSpread({}, extra.defaults()), {}, {
        checked: {
          default: false
        }
      }),
      parseDOM: [{
        tag: 'li[data-task-list-item]',
        getAttrs: node => {
          var checked = false;

          if (isElementDomNode(node) && node.getAttribute('data-checked') !== null) {
            checked = true;
          }

          return _objectSpread({
            checked
          }, extra.parse(node));
        },
        // Make sure it has higher priority then ListItemExtension's parseDOM by default
        priority: ExtensionPriority.Medium
      }, ...((_override$parseDOM = override.parseDOM) !== null && _override$parseDOM !== void 0 ? _override$parseDOM : [])],
      toDOM: node => {
        return ['li', _objectSpread(_objectSpread({}, extra.dom(node)), {}, {
          'data-task-list-item': '',
          'data-checked': node.attrs.checked ? '' : undefined
        }), 0];
      }
    });
  }

  createNodeViews() {
    return (node, view, getPos) => {
      var mark = document.createElement('input');
      mark.type = 'checkbox';
      mark.classList.add(ExtensionListTheme.LIST_ITEM_CHECKBOX);
      mark.contentEditable = 'false';
      mark.addEventListener('click', () => {
        var pos = getPos();
        var $pos = view.state.doc.resolve(pos + 1);
        this.store.commands.toggleCheckboxChecked({
          $pos
        });
        return true;
      });
      return createCustomMarkListItemNodeView({
        node,
        mark,
        updateDOM: updateNodeViewDOM,
        updateMark: updateNodeViewMark
      });
    };
  }

  createKeymap() {
    return {
      Enter: splitListItem(this.type)
    };
  }

  createExtensions() {
    return [new ListItemSharedExtension()];
  }
  /**
   * Toggles the current checkbox state and transform a normal list item into a
   * checkbox list item when necessary.
   *
   * @param checked - the `checked` attribute. If it's a boolean value, then it
   * will be set as an attribute. If it's undefined, then the `checked` attribuate
   * will be toggled.
   *
   * @param selection - a resolved position within the task list item you want to
   * toggle. It it's not passed, the lower bound of the current selection's will
   * be used.
   */


  toggleCheckboxChecked(props) {
    var checked;
    var $pos;

    if (typeof props === 'boolean') {
      checked = props;
    } else if (props) {
      checked = props.checked;
      $pos = props.$pos;
    }

    return _ref => {
      var _$pos, _checked;

      var tr = _ref.tr,
          dispatch = _ref.dispatch;
      var found = findParentNodeOfType({
        selection: (_$pos = $pos) !== null && _$pos !== void 0 ? _$pos : tr.selection.$from,
        types: this.type
      });

      if (!found) {
        return false;
      }

      var node = found.node,
          pos = found.pos;

      var attrs = _objectSpread(_objectSpread({}, node.attrs), {}, {
        checked: (_checked = checked) !== null && _checked !== void 0 ? _checked : !node.attrs.checked
      });

      dispatch === null || dispatch === void 0 ? void 0 : dispatch(tr.setNodeMarkup(pos, undefined, attrs));
      return true;
    };
  }

  createInputRules() {
    var regexp = /^\s*(\[( ?|x|X)]\s)$/;
    return [wrappingInputRule(regexp, this.type, match => {
      return {
        checked: ['x', 'X'].includes(getMatchString(match, 2))
      };
    }), new InputRule(regexp, (state, match, start, end) => {
      var tr = state.tr;
      tr.deleteRange(start, end);
      var canUpdate = wrapSelectedItems({
        listType: assertGet(this.store.schema.nodes, 'taskList'),
        itemType: this.type,
        tr
      });

      if (!canUpdate) {
        return null;
      }

      var checked = ['x', 'X'].includes(getMatchString(match, 2));

      if (checked) {
        var found = findParentNodeOfType({
          selection: tr.selection,
          types: this.type
        });

        if (found) {
          tr.setNodeMarkup(found.pos, undefined, {
            checked
          });
        }
      }

      return tr;
    })];
  }

}, (_applyDecoratedDescriptor(_class$1.prototype, "toggleCheckboxChecked", [_dec$1], Object.getOwnPropertyDescriptor(_class$1.prototype, "toggleCheckboxChecked"), _class$1.prototype)), _class$1));

function updateNodeViewDOM(node, dom) {
  node.attrs.checked ? dom.setAttribute('data-checked', '') : dom.removeAttribute('data-checked');
  dom.setAttribute('data-task-list-item', '');
}

function updateNodeViewMark(node, mark) {
  mark.checked = !!node.attrs.checked;
}

var _dec, _dec2, _class;
/**
 * Create the node for a task list.
 */

var TaskListExtension = (_dec = command({
  icon: 'listCheck',
  label: _ref => {
    var t = _ref.t;
    return t(ExtensionListMessages.TASK_LIST_LABEL);
  }
}), _dec2 = keyBinding({
  shortcut: NamedShortcut.TaskList,
  command: 'toggleTaskList'
}), (_class = class TaskListExtension extends NodeExtension {
  get name() {
    return 'taskList';
  }

  createTags() {
    return [ExtensionTag.Block, ExtensionTag.ListContainerNode];
  }

  createNodeSpec(extra, override) {
    var _override$parseDOM;

    return _objectSpread(_objectSpread({
      content: 'taskListItem+'
    }, override), {}, {
      attrs: extra.defaults(),
      parseDOM: [{
        tag: 'ul[data-task-list]',
        getAttrs: extra.parse,
        // Make sure it has higher priority then BulletListExtension's parseDOM by default
        priority: ExtensionPriority.Medium
      }, ...((_override$parseDOM = override.parseDOM) !== null && _override$parseDOM !== void 0 ? _override$parseDOM : [])],
      toDOM: node => ['ul', _objectSpread(_objectSpread({}, extra.dom(node)), {}, {
        'data-task-list': ''
      }), 0]
    });
  }

  createExtensions() {
    return [new TaskListItemExtension({})];
  }
  /**
   * Toggle the task list for the current selection.
   */


  toggleTaskList() {
    return toggleList(this.type, assertGet(this.store.schema.nodes, 'taskListItem'));
  }

  listShortcut(props) {
    return this.toggleTaskList()(props);
  }

}, (_applyDecoratedDescriptor(_class.prototype, "toggleTaskList", [_dec], Object.getOwnPropertyDescriptor(_class.prototype, "toggleTaskList"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "listShortcut", [_dec2], Object.getOwnPropertyDescriptor(_class.prototype, "listShortcut"), _class.prototype)), _class));

export { BulletListExtension, ListItemExtension, ListItemSharedExtension, OrderedListExtension, TaskListExtension, TaskListItemExtension, dedentList, indentList, sharedLiftListItem, sharedSinkListItem, toggleList, wrapSelectedItems };
